import { useState, type FormEvent } from "react";
import { api, type AdminAccountListOpts, type AdminPlan } from "../../api";
import { useAsync } from "../../useAsync";
import { StatusChip } from "../../components/StatusChip";
import { count, shortTime, DASH } from "../../format";
import { go } from "../../router";

export function AdminAccounts() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<AdminAccountListOpts["status"] | "">("");
  const [plan, setPlan] = useState<AdminPlan | "">("");
  const [applied, setApplied] = useState<AdminAccountListOpts>({ sort: "created_at", dir: "desc", limit: 50 });
  const list = useAsync(() => api.admin.accounts(applied), [applied]);

  const search = (e: FormEvent) => {
    e.preventDefault();
    setApplied({
      q: q.trim() || undefined,
      status: status || undefined,
      plan: plan || undefined,
      sort: "created_at",
      dir: "desc",
      limit: 50,
    });
  };

  return (
    <section style={{ marginTop: 18 }}>
      <form onSubmit={search} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <div className="field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="a-q">Search</label>
          <input id="a-q" className="input" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Name or billing email" />
        </div>
        <div className="field" style={{ width: 140 }}>
          <label htmlFor="a-status">Status</label>
          <select id="a-status" className="input" value={status}
                  onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">Any</option>
            <option value="trialing">Trialing</option>
            <option value="paying">Paying</option>
            <option value="free">Free</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div className="field" style={{ width: 130 }}>
          <label htmlFor="a-plan">Plan</label>
          <select id="a-plan" className="input" value={plan}
                  onChange={(e) => setPlan(e.target.value as typeof plan)}>
            <option value="">Any</option>
            <option value="free">Free</option>
            <option value="indie">Indie</option>
            <option value="growth">Growth</option>
          </select>
        </div>
        <button type="submit" className="btn-accent" style={{ minHeight: 42 }}>Filter</button>
      </form>

      {list.error && <div className="form-error">{list.error}</div>}
      {list.loading && !list.data && <div style={{ color: "var(--text-faint)" }}>Loading…</div>}
      {list.data && (
        <>
          <div className="sub" style={{ marginBottom: 10 }}>{count(list.data.total)} workspaces</div>
          <div style={{ overflowX: "auto" }}>
            <table className="ptable">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Plan</th>
                  <th>Projects</th>
                  <th>People</th>
                  <th>Sources</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {list.data.accounts.map((a) => (
                  <tr key={a.id} onClick={() => go({ name: "admin", tab: "accounts", id: a.id })}>
                    <td>
                      <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                        <span>
                          {a.name}
                          {a.disabled_at && (
                            <span className="eyebrow" style={{ marginLeft: 8, color: "var(--neg)" }}>disabled</span>
                          )}
                        </span>
                        <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{a.email}</span>
                      </div>
                    </td>
                    <td>
                      <div className="cell" style={{ textAlign: "right" }}>
                        <StatusChip
                          kind={a.plan_status === "trialing" ? "building" : a.plan_status === "active" ? "passing" : "pre-gate"}
                          label={a.plan_status === "trialing" ? "Trial" : a.plan}
                        />
                      </div>
                    </td>
                    <td><div className="cell num">{a.products}</div></td>
                    <td><div className="cell num">{a.members}</div></td>
                    <td>
                      <div className="cell num" title={`${a.connections_ok} ok / ${a.connections_error} error / ${a.connections_pending} pending`}
                           style={{ color: a.connections_error > 0 ? "var(--neg)" : undefined }}>
                        {a.connections_ok + a.connections_error + a.connections_pending === 0
                          ? DASH
                          : `${a.connections_ok}/${a.connections_ok + a.connections_error + a.connections_pending}`}
                      </div>
                    </td>
                    <td><div className="cell num">{a.last_activity ? shortTime(a.last_activity) : DASH}</div></td>
                  </tr>
                ))}
                {list.data.accounts.length === 0 && (
                  <tr><td colSpan={6}><div className="cell" style={{ color: "var(--text-4)" }}>Nothing matches.</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
